const MAPS_SCRIPT_ID = 'google-maps-js';

// Reused across mounts so the Google Maps script is only ever injected once.
let mapsLoaderPromise = null;

/**
 * Lazily loads the Google Maps JavaScript API (mirrors Team Phoenix's clinic
 * finder in mosaic): inject the script once and resolve when it is ready.
 * Subsequent calls reuse the same promise so the script is never loaded twice.
 *
 * Google resolves its own strings (zoom tooltips, Terms, "Report a map error")
 * from `language` on the script url, defaulting to the visitor's browser when
 * it is absent - so the form's language is passed to keep the map in step with
 * the rest of the page. Google reads it once at load, and the cached promise
 * means the first caller's language stands for the whole page. That is fine
 * while a form has one locale.
 *
 * @param {string} apiKey - browser (referrer-restricted) Maps JS API key
 * @param {string} [language] - the form's language (e.g. `es`); omitted if absent
 * @returns {Promise<void>}
 */
export const loadGoogleMapsScript = (apiKey, language) => {
  if (window.google && window.google.maps) {
    return Promise.resolve();
  }

  if (mapsLoaderPromise) {
    return mapsLoaderPromise;
  }

  mapsLoaderPromise = new Promise((resolve, reject) => {
    const languageParam = language ? `&language=${language}` : '';
    const script = document.createElement('script');
    script.id = MAPS_SCRIPT_ID;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}${languageParam}`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => {
      // Allow a later retry if the network request failed.
      mapsLoaderPromise = null;
      reject(new Error('Failed to load the Google Maps API'));
    };
    document.head.appendChild(script);
  });

  return mapsLoaderPromise;
};

/**
 * Builds a data-URI SVG marker for the selected store: a teardrop map pin (the
 * same shape as Google's default marker) in the theme colour, with a
 * translucent halo ("glow") behind its head and the bold store number. The pin
 * tip sits at the bottom centre (20, 46) so it should be anchored there.
 *
 * @param {number|string} number - the store's 1-based position (pin label)
 * @param {string} color - the theme accent colour (any CSS colour string)
 * @returns {string} a `data:image/svg+xml` URL
 */
export const buildGlowMarkerSvgUrl = (number, color) => {
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="40" height="52" viewBox="0 0 40 52">` +
    `<circle cx="20" cy="18" r="17" fill="${color}" fill-opacity="0.16"/>` +
    `<circle cx="20" cy="18" r="13" fill="${color}" fill-opacity="0.3"/>` +
    `<path d="M20 4C12.8 4 7 9.8 7 17c0 9.75 13 29 13 29s13-19.25 13-29C33 9.8 27.2 4 20 4z" fill="${color}"/>` +
    `<text x="20" y="17" text-anchor="middle" dominant-baseline="central" font-family="Arial, sans-serif" font-size="13" font-weight="700" fill="#ffffff">${number}</text>` +
    `</svg>`;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
};
